"use client";

import { Button } from "@stockline/ui/components/button";
import { Loader2, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";

type BootRecoveryPanelProps = Readonly<{
  /** IndexedDB reported the DB blocked (multi-tab / mid-deploy upgrade). */
  blocked: boolean;
  /**
   * The watchdog is auto-recovering (a prior restart didn't clear the wedge),
   * so show a quiet progress state instead of the button.
   */
  autoRecovering: boolean;
  /** Hard reload the tab. */
  restart: () => void;
}>;

/**
 * Escape hatch shown once the boot watchdog decides the splash is stuck (see
 * `useBootRecovery`). Deliberately minimal: one line of context and a single
 * Restart button. A restart that STILL lands stalled is escalated to a local
 * reset automatically by the watchdog, so the user only ever sees one action.
 */
export function BootRecoveryPanel({
  blocked,
  autoRecovering,
  restart,
}: BootRecoveryPanelProps) {
  const { t } = useTranslation();

  if (autoRecovering) {
    return (
      <div
        data-testid="boot-recovery"
        className="animate-slide-in flex w-full flex-col items-center gap-3 text-center"
      >
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        <p className="text-xs text-gray-500">
          {t("common.boot.recovery.autoBody", {
            defaultValue:
              "Still stuck — clearing this device's cached data (any unsynced changes are lost) and restarting. You stay signed in.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="boot-recovery"
      className="animate-slide-in flex w-full flex-col items-center gap-4 text-center"
    >
      <p className="text-sm text-gray-500">
        {blocked
          ? t("common.boot.recovery.blockedBody", {
              defaultValue:
                "Another tab may be blocking startup. Restarting usually fixes it.",
            })
          : t("common.boot.recovery.body", {
              defaultValue: "This is taking longer than expected.",
            })}
      </p>
      <Button
        type="button"
        onClick={restart}
        className="w-full"
        data-testid="boot-recovery-restart"
      >
        <RotateCw className="mr-2 h-4 w-4" />
        {t("common.boot.recovery.restart", { defaultValue: "Restart" })}
      </Button>
    </div>
  );
}
